import { useState } from "react";

type FeedMedia = {
  local_path: string;
  cdn_url?: string;
  media_type: string;
};

type FeedPost = {
  id: string;
  caption: string;
  permalink: string;
  timestamp: string;
  media: FeedMedia[];
};

function CaptionText({ text }: { text: string }) {
  const parts = text.split(/(#[^\s#]+)/g);
  
  return (
    <p className="whitespace-pre-wrap leading-relaxed">
      {parts.map((part, index) => {
        if (part.startsWith('#')) {
          return (
            <span key={index} className="text-blue-500 dark:text-blue-400">
              {part}
            </span>
          );
        }
        return part;
      })}
    </p>
  );
}

function ImageCarousel({ media, postId, altText }: { media: FeedMedia[]; postId: string; altText: string }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? media.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === media.length - 1 ? 0 : prev + 1));
  };

  if (media.length === 0) {
    return (
      <div className="w-full aspect-square bg-muted flex items-center justify-center text-muted-foreground">
        No media
      </div>
    );
  }

  const currentMedia = media[currentIndex]!;
  const mediaSrc = currentMedia.cdn_url ?? `/${currentMedia.local_path}`;
  const isVideo = currentMedia.media_type === "video";

  return (
    <div className="relative w-full aspect-square bg-black group">
      {isVideo ? (
        <video
          src={mediaSrc}
          controls
          className="w-full h-full object-contain"
          preload="metadata"
          aria-label={altText}
        />
      ) : (
        <img
          src={mediaSrc}
          alt={altText}
          className="w-full h-full object-contain"
          loading="lazy"
          width="1080"
          height="1080"
        />
      )}

      {media.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
            aria-label="Previous image"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={goToNext}
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
            aria-label="Next image"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
            {media.map((_, index) => (
              <button
                key={`${postId}-dot-${index}`}
                onClick={() => setCurrentIndex(index)}
                className={`w-1.5 h-1.5 rounded-full transition-all ${
                  index === currentIndex ? "bg-white w-2" : "bg-white/60"
                }`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>

          <div className="absolute top-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
            {currentIndex + 1} / {media.length}
          </div>
        </>
      )}
    </div>
  );
}

function formatDate(timestamp: string): string {
  const date = new Date(timestamp);
  const month = date.toLocaleString('en-US', { month: 'long' });
  const day = date.getDate();
  const year = date.getFullYear();
  
  const suffix = (day: number) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };
  
  return `${month} ${day}${suffix(day)}, ${year}`;
}

const generateSlug = (caption: string, id: string): string => {
  if (!caption) return id;

  const words = caption
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .split(/\s+/)
    .slice(0, 5)
    .join('-');

  return `${words}-${id}`;
};

const postPath = (post: FeedPost): string => `/posts/${generateSlug(post.caption, post.id)}`;

const postTitle = (post: FeedPost): string => {
  const cleanCaption = post.caption ? post.caption.replace(/\s+/g, ' ').trim() : "";
  return cleanCaption
    ? `${cleanCaption.slice(0, 88)}${cleanCaption.length > 88 ? "..." : ""}`
    : "Wine tasting note";
};

export function SinglePost({
  post,
  newerPost,
  olderPost,
}: {
  post: FeedPost;
  newerPost?: FeedPost;
  olderPost?: FeedPost;
}) {
  // Generate alt text from caption (same as og:title)
  const cleanCaption = post.caption ? post.caption.replace(/\s+/g, ' ').trim() : "";
  const altText = cleanCaption 
    ? `${cleanCaption.slice(0, 60)}${cleanCaption.length > 60 ? "..." : ""}`
    : "Wine tasting note";
  const headline = postTitle(post);
  const isoDate = new Date(post.timestamp).toISOString();

  return (
    <div className="max-w-2xl mx-auto">
      <article className="rounded-lg border bg-card text-card-foreground shadow-lg overflow-hidden" itemScope itemType="https://schema.org/BlogPosting">
        <h1 className="sr-only" itemProp="headline">{headline}</h1>
        <meta itemProp="dateModified" content={isoDate} />
        <ImageCarousel media={post.media} postId={post.id} altText={altText} />
        
        <div className="p-6 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm text-muted-foreground mb-3">
                <time dateTime={isoDate} itemProp="datePublished">{formatDate(post.timestamp)}</time>
              </p>
              <div itemProp="articleBody">
                {post.caption ? (
                  <CaptionText text={post.caption} />
                ) : (
                  <p className="text-muted-foreground">No caption</p>
                )}
              </div>
            </div>
          </div>
          
          <a
            href={post.permalink}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center text-sm font-medium text-blue-500 hover:text-blue-600 transition-colors"
          >
            View on Instagram
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </article>

      {(newerPost || olderPost) && (
        <nav className="mt-6 grid gap-3 sm:grid-cols-2" aria-label="More tasting notes">
          {newerPost && (
            <a
              href={postPath(newerPost)}
              className="block rounded-lg border bg-card p-4 text-sm shadow-sm transition-colors hover:bg-muted"
            >
              <span className="block text-muted-foreground">Newer note</span>
              <span className="mt-1 block font-medium text-foreground">{postTitle(newerPost)}</span>
            </a>
          )}
          {olderPost && (
            <a
              href={postPath(olderPost)}
              className="block rounded-lg border bg-card p-4 text-sm shadow-sm transition-colors hover:bg-muted"
            >
              <span className="block text-muted-foreground">Older note</span>
              <span className="mt-1 block font-medium text-foreground">{postTitle(olderPost)}</span>
            </a>
          )}
        </nav>
      )}
    </div>
  );
}
